/** @module Auth0ErrorTranslationLayer */
/* eslint-disable import/extensions */
import logger from './logging/auth0-logger.mjs';

/**
 * @object
 * @description Add translatable resources to this array. For each resource, add the `source` of the error, a `fragment` string that can be used to identify the original error as issued by `source`, the resource ID `key` as exists in `messages.js` and finally the string method to be used for match. Can be `includes`, `startsWith`, `endsWith`, `indexOf` or `match`
 */
const ERROR_FRAGMENTS = [
  {
    source: 'Auth0',
    fragment: 'Webtask exceeded allowed execution time',
    key: 'error-timeout-response',
    match: 'includes',
  },
  {
    source: 'Auth0',
    fragment: 'Access expired.',
    key: 'error-change-password-expired-link',
    match: 'equals',
  },
  {
    source: 'Auth0',
    fragment: 'Ambiguous user: change password',
    key: 'error-change-password-ambiguous-user',
    match: 'equals',
  },
  {
    source: 'Auth0',
    fragment: 'Ambiguous user: change email',
    key: 'error-ambiguous-user-generic',
    match: 'equals',
  },
  {
    source: 'Auth0',
    fragment: 'Ambiguous user: verify email',
    key: 'error-ambiguous-user-generic',
    match: 'equals',
  },
  {
    source: 'Auth0',
    fragment: 'Ambiguous user',
    key: 'error-ambiguous-user-generic',
    match: 'equals',
  },
  {
    source: 'Auth0',
    fragment: 'combination of credentials was detected in a public data breach',
    key: 'error-change-password-breached-password',
    match: 'includes',
  },
  {
    source: 'Auth0',
    fragment: 'login attempt has been blocked',
    key: 'login-error-breached-password',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'Your account has been deactivated',
    key: 'error-account-deactivated',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'multiple users with the email address provided',
    key: 'error-multiple-emails',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'username or password you entered was invalid',
    key: 'error-username-password',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'note that repeated failed attempts will result in your account being locked out',
    key: 'error-username-password-lockout',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'currently performing maintenance on your account',
    key: 'error-performing-maintenance',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'Partition Maintenance In Progress',
    key: 'error-performing-maintenance',
    match: 'equals',
  },
  {
    source: 'WebAPI',
    fragment: 'Seller Maintenance',
    key: 'error-performing-maintenance',
    match: 'equals',
  },
  {
    source: 'WebAPI',
    fragment: 'unexpected error occurred during login',
    key: 'error-login-unexpected',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'user account has not been authorized to login to this workstation',
    key: 'error-workstation-auth',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'user account has been locked due to too many failed login attempts',
    key: 'error-user-locked-out',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'account is locked out, please contact support to login',
    key: 'error-user-locked-out-2',
    match: 'includes',
  },
  {
    source: 'WebAPI',
    fragment: 'Seller Locked Out; Contact Support',
    key: 'error-user-locked-out-2',
    match: 'equals',
  },
  {
    source: 'WebAPI',
    fragment: 'email address has not yet been verified',
    key: 'error-email-verification',
    match: 'includes',
  },
  {
    source: 'IdentityService',
    fragment: 'Password change request uses a password from your recent password history',
    key: 'error-change-password-history',
    match: 'includes',
  },
];

/**
 * @static
 * @description A lot of errors returned by Auth0 and WebAPI are English-only. This is a simple detection layer that allows us to convert those errors into translatable resources before display
 */
class Auth0ErrorTranslationLayer {
  static convert = (originalError) => {
    if (!originalError) {
      return { matched: false };
    }

    originalError = this._extractMessageTextFromError(originalError);
    const match = this._match(originalError);
    if (match.length === 0) {
      return {
        matched: false,
        errorText: originalError,
      };
    }

    if (match.length > 1) {
      logger.warn(`Error translation layer returned multiple matches for error "${originalError}". Using first.`);
    }

    const fragmentResourceMatch = match.pop();
    console.log(`Matched error text "${originalError}" with resource key "${fragmentResourceMatch.key}"`);
    return {
      matched: true,
      errorText: originalError,
      key: fragmentResourceMatch.key,
    };
  };

  static _extractMessageTextFromError = (err) => {
    if (!err) return '';
    console.log(`_extractMessageTextFromError > ${JSON.stringify(err)}`);
    const msgIsString = typeof err === 'string';
    const msgIsObject = typeof err === 'object';
    return msgIsObject && !msgIsString
      ? err.message || err.description || err.error || err.error_description
      : err;
  };

  static _match = (errorText) => {
    if (!errorText) return [];
    return ERROR_FRAGMENTS.filter((f) => {
      switch (f.match) {
        case 'equals':
          return errorText === f.fragment;
        case 'startsWith':
          return errorText.startsWith(f.fragment);
        case 'endsWith':
          return errorText.endsWith(f.fragment);
        case 'indexOf':
          return errorText.indexOf(f.fragment) === f.matchArgs;
        case 'match':
          return errorText.match(f.fragment);
        case 'include':
        default:
          return errorText.includes(f.fragment);
      }
    });
  };
}

export default Auth0ErrorTranslationLayer;
